<template>
    <div class="sso-join-form">
        <div class="sso-join-form__container">
            <div class="sso-join-form__left-side">
                <div class="sso-join-form__inner">
                    <h1 class="sso-join-form__header" v-html="$t('SSO.join_form_header')"></h1>
                    <span class="sso-join-form__description" v-html="$t('SSO.join_form_description')"></span>
                    <template v-if="error !== null">
                        <div class="sso-join-form__error" v-html="error"></div>
                    </template>
                    <template v-else>
                        <template v-if="!signed">
                            <TotemInput :value="ssoUserData.firstName" :label="$t('General.first_name')" isRequired fullWidth disabled />
                            <TotemInput :value="ssoUserData.lastName" :label="$t('General.last_name')" isRequired fullWidth disabled />
                            <TotemInput :value="ssoUserData.email" :label="$t('General.email_address')" isRequired fullWidth disabled />

                            <div class="sso-join-form__terms">
                                <TotemCheckbox class="sso-join-form__terms-checkbox" v-model="$v.terms.$model" name="terms" />
                                <TotemInputLabel isRequired class="sso-join-form__terms-label" v-html="$t('General.terms_and_policy_info_updated')">
                                </TotemInputLabel>
                            </div>
                            <totem-form-finnish-label-error
                                class="sso-join-form__terms-error"
                                :field="$v.terms" v-if="!$v.terms.$model"
                                :label="$t('General.terms_and_policy')"
                            />
                            <div>
                                <TotemButton
                                    @click="sendForm"
                                    color="primary"
                                    class="sso-join-form__submit-button"
                                    :disabled="sending"
                                    :label="$t('SSO.join_form_submit_button')"
                                />
                            </div>
                        </template>
                        <div class="sso-join-form__thank-you" v-else v-html="$t('SSO.join_form_thank_you_info')"></div>
                    </template>
                </div>
            </div>
            <div class="sso-join-form__right-side">
                <div class="sso-join-form__inner">
                    <img src="@/assets/images/agreement-form.png" class="sso-join-form__image" alt="" v-if="isDesktopView" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import { validationMixin } from 'vuelidate'
    import sortAlphabeticallyAscByJobTitle from 'components/shared/mixins/sorting/sortAlphabeticallyAscByJobTitle'
    const { sameAs } = require('vuelidate/lib/validators')

    export default {
        name: 'sso-join-form',
        mixins: [ validationMixin, sortAlphabeticallyAscByJobTitle ],
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                ssoUserData: 'getSSOUserData',
                joinToken: 'getJoinToken',
                authorizationCode: 'getSSOAuthorizationCode'
            }),
        },
        validations: {
            terms: {
                checkboxRequired: sameAs(() => true)
            },
        },
        watch: {
            isLoggedIn: {
                handler (logged) {
                    if (logged) {
                        this.$router.push({ name: 'home' })
                    }
                },
                immediate: true
            }
        },
        data () {
            return {
                triedSubmit: false,
                error: null,
                terms: false,
                signed: false,
                sending: false,
                trialFormData: {},
            }
        },
        created () {
            if (this.joinToken === null) {
                this.error = 'To join using Microsoft login you must have valid join token. <br/> Please use "Sign in with Microsoft" option from menu.'
            }
        },
        methods: {
            handleError (responseCode) {
                this.sending = false
                if (responseCode === 621) {
                    this.error = this.$t('General.already_have_account')
                    return
                }

                this.error = this.$t('General.sth_went_wrong')
            },
            async sendForm (e) {
                this.error = null
                this.$v.$touch()
                if (this.$v.$invalid) {
                    e.preventDefault()
                } else {
                    this.sending = true
                    try {
                        await this.$store.dispatch('User/joinByToken', this.joinToken)
                        await this.$store.dispatch('clearSsoJoinData', this.joinToken)
                        this.signed = true
                        await this.$store.dispatch('loginWithSSOAuthorizationCode', {
                            code: this.authorizationCode.code,
                            providerId: this.authorizationCode.providerId
                        })
                        await this.$store.dispatch('clearSsoAuthorizationCode')
                    } catch (exc) {
                        console.log(exc)
                        this.handleError(exc.code)
                    }
                }
            }
        },
    }
</script>

<style lang="scss" scoped>
    .sso-join-form {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 0 auto;

        [class*="desktop"] & {
            flex-direction: row;
        }

        &__container {
            display: flex;
            flex-direction: column;
            margin: 0 auto;
            justify-content: center;

            [class*="desktop"] & {
                flex-direction: row;
                padding: 80px 20px 30px;
            }

            [class*="mobile"] & {
                padding: 30px;
            }
        }

        &__left-side,
        &__right-side {
            display: flex;
        }

        &__right-side {
            padding: 0 0 0 60px;
        }

        &__left-side {
            flex-direction: column;
            padding: 0;
            display: flex;

            [class*="desktop"] & {
                width: 750px;
            }
        }

        &__inner {
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            gap: 10px;
            padding: 0 0 15px;
        }

        &__image {
            width: 100%;
            max-width: 400px;
        }

        &__header {
            margin: 0 0 10px;
            font-family: PPAgrandir-Medium, sans-serif;
            color: $lavender;
        }

        &__description {
            margin: 0 0 20px;
        }

        &__terms {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        &__terms-label {
            margin: 0;
        }

        &__terms-label::v-deep a {
            color: $lavender;
            cursor: pointer;
            text-decoration: underline;
        }

        &__terms-checkbox {
            margin-top: 4px !important;
        }

        &__terms-error {
            padding: 0;
        }

        &__submit-button {
            display: flex;
            line-height: 30px;
            margin: 10px auto 0;

            [class*="desktop"] & {
                margin: 10px 0 0;
            }
        }

        &__thank-you {
            font-size: 3rem;
        }

        &__error {
            margin: 20px 0;
            color: $chestnut;
            line-height: 150%;
        }
    }
</style>
